import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { buildJwt } from './TokenGenerator';
import api from '../services/api';

const AuthContext = createContext(undefined);

// Token refresh interval - refresh JWT 5 minutes before expiry (at 55 minutes)
const TOKEN_REFRESH_INTERVAL = 55 * 60 * 1000; // 55 minutes in milliseconds

// Token expiry time - tokens expire after 1 hour
const TOKEN_EXPIRY_TIME = 60 * 60 * 1000; // 1 hour in milliseconds

// User inactivity threshold - consider user inactive after 5 minutes of no activity
const INACTIVITY_THRESHOLD = 10 * 60 * 1000; // 10 minutes in milliseconds

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// API endpoint for allowed domains


// Get environment for API query
// const getEnvParam = () => {
//   const env = process.env.REACT_APP_SERVER_ENVIRONMENT;
//   if (env === 'stage') return 'stage';
//   if (env === 'development' || env === 'dev') return 'dev';
//   if (env === 'prod' || env === 'production') return 'prod';
//   return 'local';
// };

// // Fetch allowed domains from API
// const fetchAllowedDomains = async () => {
//   try {
//     const env = getEnvParam();
//     // const response = await fetch(`${ALLOWED_DOMAINS_API}?env=${env}&guest=${process.env.REACT_APP_BYPASS_AUTH}`);
//     const response = await fetch(`${ALLOWED_DOMAINS_API}?env=${env}&guest=${process.env.REACT_APP_BYPASS_AUTH}`, {
//         headers: { Authorization: `Bearer ${accessToken}` },
//       });
//     if (!response.ok) throw new Error('Failed to fetch allowed domains');
//     const data = await response.json();
//     return data.map(item => item.domain.toLowerCase());
//   } catch (error) {
//     console.error('Error fetching allowed domains:', error);
//     return null;
//   }
// };

// Extract domain from email
const getEmailDomain = (email) => {
  if (!email || !email.includes('@')) return null;
  return email.split('@')[1].toLowerCase();
};

// Check if auth bypass is enabled
const BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

// Check if running on staging environment
const IS_STAGING = process.env.REACT_APP_SERVER_ENVIRONMENT === 'stage';

// On staging with bypass auth, auto-bypass without showing test user dropdown
const SHOULD_AUTO_BYPASS = BYPASS_AUTH && IS_STAGING;

// Test users for bypass mode - different users for staging vs dev
const DEV_TEST_USERS = [
  { id: '100000000000000000001', email: 'dev@pixacore.com', name: 'Dev User' },
  { id: '100000000000000000002', email: 'qa@pixacore.com', name: 'QA User' },
  { id: '100000000000000000003', email: 'bhoomi@pixacore.com', name: 'Bhoomi' },
  { id: '100000000000000000004', email: 'amit@pixacore.com', name: 'Amit' },
];

const STAGING_TEST_USERS = [
  { id: '100000000000000000005', email: 'tien@gene.com', name: 'Tien' },
];

// Use staging users on staging environment, otherwise use dev users
const TEST_USERS = IS_STAGING ? STAGING_TEST_USERS : DEV_TEST_USERS;

// Create mock user from test user email
const createMockUser = (email) => {
  const testUser = TEST_USERS.find(u => u.email === email);
  if (testUser) {
    return {
      id: testUser.id,
      email: testUser.email,
      name: testUser.name,
      avatar_url: null,
    };
  }
  // Default fallback
  return {
    id: '000000000000000000001',
    email: email || 'developer@example.com',
    name: email ? email.split('@')[0] : 'Dev User',
    avatar_url: null,
  };
};

// Mock token for bypass mode
const MOCK_TOKEN = 'bypass-token';

// Storage keys
const TOKEN_STORAGE_KEY = 'google_id_token';
const REFRESH_TOKEN_STORAGE_KEY = 'google_refresh_token';
const TOKEN_EXPIRY_STORAGE_KEY = 'google_token_expiry';
const USER_STORAGE_KEY = 'google_user';
const IS_GUEST_STORAGE_KEY = 'is_guest_user';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [jwtToken, setjwtToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [authError, setAuthError] = useState(null);
  const refreshTimerRef = useRef(null);
  const googleTokenRefreshTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const tokenExpiryRef = useRef(null);
  const activityCheckTimerRef = useRef(null);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check if user is currently active
  const isUserActive = useCallback(() => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    return timeSinceActivity < INACTIVITY_THRESHOLD;
  }, []);

  // Function to refresh JWT token
  const refreshJwtToken = useCallback(async () => {
    try {
      const newJwtToken = await buildJwt();
      setjwtToken(newJwtToken);
      tokenExpiryRef.current = Date.now() + TOKEN_EXPIRY_TIME;
      console.log('JWT token refreshed successfully');
      return newJwtToken;
    } catch (error) {
      console.error('Error refreshing JWT token:', error);
      return null;
    }
  }, []);

  // Start the token refresh timer
  const startTokenRefreshTimer = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }

    // Set up new timer to refresh token every 55 minutes
    refreshTimerRef.current = setInterval(() => {
      // Check if user is active before refreshing
      if (isUserActive()) {
        console.log('User is active, auto-refreshing JWT token...');
        refreshJwtToken();
      } else {
        console.log('User is inactive, skipping token refresh');
      }
    }, TOKEN_REFRESH_INTERVAL);
  }, [refreshJwtToken, isUserActive]);

  // Stop the token refresh timer
  const stopTokenRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Handle expired inactive sessions
  const handleExpiredInactiveSession = useCallback(() => {
    const now = Date.now();
    const tokenExpiry = tokenExpiryRef.current;

    // If token has expired
    if (tokenExpiry && now > tokenExpiry) {
      // Check if user is inactive
      if (!isUserActive()) {
        console.log('Token expired and user is inactive, logging out...');
        // Clear state - this will trigger cleanup in other effects
        setUser(null);
        setToken(null);
        setRefreshToken(null);
        setIsGuestUser(false);
        // Clear storage
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        localStorage.removeItem(IS_GUEST_STORAGE_KEY);
      } else {
        // User is active but token expired, refresh it
        console.log('Token expired but user is active, refreshing...');
        refreshJwtToken();
      }
    }
  }, [isUserActive, refreshJwtToken]);

  // Start activity monitoring and token expiry checks
  const startActivityMonitoring = useCallback(() => {
    // Clear any existing timer
    if (activityCheckTimerRef.current) {
      clearInterval(activityCheckTimerRef.current);
    }

    // Check token expiry and user activity every 30 seconds
    activityCheckTimerRef.current = setInterval(() => {
      handleExpiredInactiveSession();
    }, 30 * 1000); // Check every 30 seconds
  }, [handleExpiredInactiveSession]);

  // Stop activity monitoring
  const stopActivityMonitoring = useCallback(() => {
    if (activityCheckTimerRef.current) {
      clearInterval(activityCheckTimerRef.current);
      activityCheckTimerRef.current = null;
    }
  }, []);

  // Function to refresh Google access token using refresh token
  const refreshGoogleAccessToken = useCallback(async (storedRefreshToken) => {
    const refreshTokenToUse = storedRefreshToken || localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!refreshTokenToUse) {
      console.log('No refresh token available');
      return null;
    }

    try {
      // Call Google's token endpoint to get a new access token
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: GOOGLE_CLIENT_ID,
          grant_type: 'refresh_token',
          refresh_token: refreshTokenToUse,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to refresh Google token:', errorData);
        // If refresh token is invalid, clear stored tokens and force re-login
        if (errorData.error === 'invalid_grant') {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
          localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
          localStorage.removeItem(USER_STORAGE_KEY);
          localStorage.removeItem(IS_GUEST_STORAGE_KEY);
          setUser(null);
          setToken(null);
          setRefreshToken(null);
        }
        return null;
      }

      const tokenData = await response.json();
      const newAccessToken = tokenData.access_token;
      const expiresIn = tokenData.expires_in || 3600; // Default to 1 hour
      const newExpiry = Date.now() + (expiresIn * 1000);

      // Update state and storage
      setToken(newAccessToken);
      localStorage.setItem(TOKEN_STORAGE_KEY, newAccessToken);
      localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, newExpiry.toString());

      // If a new refresh token is provided (rare, but possible), update it
      if (tokenData.refresh_token) {
        setRefreshToken(tokenData.refresh_token);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokenData.refresh_token);
      }

      console.log('Google access token refreshed successfully');
      return newAccessToken;
    } catch (error) {
      console.error('Error refreshing Google access token:', error);
      return null;
    }
  }, []);

  // Start the Google token refresh timer
  const startGoogleTokenRefreshTimer = useCallback(() => {
    // Clear any existing timer
    if (googleTokenRefreshTimerRef.current) {
      clearInterval(googleTokenRefreshTimerRef.current);
    }

    // Check token expiry and refresh if needed
    const checkAndRefreshToken = async () => {
      const storedExpiry = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

      if (!storedRefreshToken) {
        return; // No refresh token, can't auto-refresh
      }

      // Refresh if token expires in less than 5 minutes
      const expiryTime = storedExpiry ? parseInt(storedExpiry, 10) : 0;
      const timeUntilExpiry = expiryTime - Date.now();

      if (timeUntilExpiry < 5 * 60 * 1000) { // Less than 5 minutes
        console.log('Google access token expiring soon, refreshing...');
        await refreshGoogleAccessToken(storedRefreshToken);
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Set up interval to check every 5 minutes
    googleTokenRefreshTimerRef.current = setInterval(checkAndRefreshToken, 5 * 60 * 1000);
  }, [refreshGoogleAccessToken]);

  // Stop the Google token refresh timer
  const stopGoogleTokenRefreshTimer = useCallback(() => {
    if (googleTokenRefreshTimerRef.current) {
      clearInterval(googleTokenRefreshTimerRef.current);
      googleTokenRefreshTimerRef.current = null;
    }
  }, []);

  // Transform Google user info to our app's user format
  const transformGoogleUser = (googleUser) => {
    if (!googleUser) return null;
    return {
      id: googleUser.sub,
      email: googleUser.email || '',
      name: googleUser.name || googleUser.email?.split('@')[0] || 'User',
      avatar_url: googleUser.picture,
    };
  };

  // Fetch user info from Google using access token
  const fetchGoogleUserInfo = useCallback(async (accessToken) => {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch user info');
      return await response.json();
    } catch (error) {
      console.error('Error fetching Google user info:', error);
      return null;
    }
  }, []);

  // Initialize from stored session
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);
        const storedIsGuest = localStorage.getItem(IS_GUEST_STORAGE_KEY);
        const authJwtToken = await buildJwt();
        setjwtToken(authJwtToken);
        tokenExpiryRef.current = Date.now() + TOKEN_EXPIRY_TIME;

        // Start the JWT token refresh timer
        startTokenRefreshTimer();

        // Start activity monitoring
        startActivityMonitoring();

        if (storedToken && storedUser) {
          // For guest users (mock token), restore immediately without verification
          if (storedToken === MOCK_TOKEN) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            setIsGuestUser(storedIsGuest === 'true');
            return;
          }

          // For real Google OAuth tokens, verify they're still valid
          const response = await fetch(
            `https://oauth2.googleapis.com/tokeninfo?access_token=${storedToken}`
          );

          if (response.ok) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            setIsGuestUser(storedIsGuest === 'true');
            if (storedRefreshToken) {
              setRefreshToken(storedRefreshToken);
              // Start the Google token refresh timer
              startGoogleTokenRefreshTimer();
            }
          } else {
            // Token expired, try to refresh using refresh token
            if (storedRefreshToken) {
              console.log('Access token expired, attempting to refresh...');
              const newAccessToken = await refreshGoogleAccessToken(storedRefreshToken);
              if (newAccessToken) {
                setToken(newAccessToken);
                setUser(JSON.parse(storedUser));
                setIsGuestUser(storedIsGuest === 'true');
                setRefreshToken(storedRefreshToken);
                // Start the Google token refresh timer
                startGoogleTokenRefreshTimer();
              } else {
                // Refresh failed, clear storage
                localStorage.removeItem(TOKEN_STORAGE_KEY);
                localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
                localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
                localStorage.removeItem(USER_STORAGE_KEY);
                localStorage.removeItem(IS_GUEST_STORAGE_KEY);
              }
            } else {
              // No refresh token, clear storage
              localStorage.removeItem(TOKEN_STORAGE_KEY);
              localStorage.removeItem(USER_STORAGE_KEY);
              localStorage.removeItem(IS_GUEST_STORAGE_KEY);
            }
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        localStorage.removeItem(IS_GUEST_STORAGE_KEY);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Cleanup timers on unmount
    return () => {
      stopTokenRefreshTimer();
      stopGoogleTokenRefreshTimer();
      stopActivityMonitoring();
    };
  }, [startTokenRefreshTimer, stopTokenRefreshTimer, startGoogleTokenRefreshTimer, stopGoogleTokenRefreshTimer, refreshGoogleAccessToken, startActivityMonitoring, stopActivityMonitoring]);

  // Set up activity tracking event listeners
  useEffect(() => {
    // Only track activity when user is logged in
    if (!user) return;

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Cleanup event listeners
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user, updateActivity]);

  // Exchange authorization code for tokens via backend
  const exchangeCodeForTokens = useCallback(async (code) => {
    try {
      const authJwtToken = await buildJwt();
      const env = process.env.REACT_APP_SERVER_ENVIRONMENT || 'dev';
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE_URL}/api/exchange-google-token?env=${env}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authJwtToken}`,
        },
        body: JSON.stringify({
          code: code,
          redirect_uri: window.location.origin,
          env: env,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to exchange code for tokens:', errorData);
        throw new Error('Failed to exchange code for tokens');
      }

      return await response.json();
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      return null;
    }
  }, []);

  // Google login hook - using auth-code flow for refresh tokens
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      try {
        setAuthError(null); // Clear any previous errors

        // Exchange the authorization code for tokens
        const tokenData = await exchangeCodeForTokens(codeResponse.code);
        if (!tokenData || !tokenData.access_token) {
          throw new Error('Failed to get tokens from authorization code');
        }

        const accessToken = tokenData.access_token;
        const refreshTokenValue = tokenData.refresh_token;
        const expiresIn = tokenData.expires_in || 3600;
        const tokenExpiry = Date.now() + (expiresIn * 1000);

        // Fetch user info
        const googleUserInfo = await fetchGoogleUserInfo(accessToken);
        if (!googleUserInfo) {
          throw new Error('Failed to get user info');
        }

        const transformedUser = transformGoogleUser(googleUserInfo);

        // Validate email domain against allowed domains
        const userDomain = getEmailDomain(transformedUser.email);
        const authJwtToken = await buildJwt();
        const allowedDomainsData = await api.fetchAllowedDomains(accessToken, authJwtToken);
        const allowedDomains = allowedDomainsData.map(item => item.domain.toLowerCase())

        if (allowedDomains && userDomain) {
          const isDomainAllowed = allowedDomains.includes(userDomain);
          if (!isDomainAllowed) {
            // Revoke the token since user is not authorized
            try {
              await fetch(`https://oauth2.googleapis.com/revoke?token=${accessToken}`, {
                method: 'POST',
              });
            } catch (revokeError) {
              console.error('Error revoking token:', revokeError);
            }
            setAuthError('Not authorized. Please reach out to the administrator OR try using your company or organization email.');
            return;
          }
        } else if (!allowedDomains) {
          // If we couldn't fetch allowed domains, deny access for safety
          setAuthError('Unable to verify authorization. Please try again later.');
          return;
        }

        // Store in state
        setToken(accessToken);
        setUser(transformedUser);
        setIsGuestUser(false); // Real Google login is never a guest
        if (refreshTokenValue) {
          setRefreshToken(refreshTokenValue);
        }

        // Set token expiry for activity monitoring
        tokenExpiryRef.current = Date.now() + TOKEN_EXPIRY_TIME;

        // Persist to localStorage
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(transformedUser));
        localStorage.setItem(IS_GUEST_STORAGE_KEY, 'false');
        localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, tokenExpiry.toString());
        if (refreshTokenValue) {
          localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshTokenValue);
          // Start the Google token refresh timer
          startGoogleTokenRefreshTimer();
        }
      } catch (error) {
        console.error('Login error:', error);
        setAuthError('An error occurred during login. Please try again.');
      }
    },
    onError: (error) => {
      console.error('Google login error:', error);
      setAuthError('Google login failed. Please try again.');
    },
    scope: 'email profile openid',
    // Request offline access to get refresh token
    // Note: prompt: 'consent' forces consent screen to always show, ensuring refresh token is returned
    // access_type: 'offline' is set by using flow: 'auth-code'
  });

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  const signInWithGoogle = useCallback(async (testUserEmail = null) => {
    setAuthError(null); // Clear any previous errors
    // If bypass auth is enabled and test user is selected (not empty)
    if (BYPASS_AUTH && testUserEmail && testUserEmail.trim() !== '') {
      // Use mock login with selected test user (guest mode)
      const mockUser = createMockUser(testUserEmail);
      setUser(mockUser);
      setToken(MOCK_TOKEN);
      setIsGuestUser(true); // Mark as guest user

      // Set token expiry for activity monitoring
      tokenExpiryRef.current = Date.now() + TOKEN_EXPIRY_TIME;

      // Persist to localStorage
      localStorage.setItem(TOKEN_STORAGE_KEY, MOCK_TOKEN);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(mockUser));
      localStorage.setItem(IS_GUEST_STORAGE_KEY, 'true');
      return;
    }

    // Use actual Google OAuth login
    // This happens when:
    // 1. BYPASS_AUTH is false (production mode)
    // 2. BYPASS_AUTH is true but no test user selected (allows real Google login in bypass mode)
    setIsGuestUser(false); // Mark as non-guest user
    googleLogin();
  }, [googleLogin]);

  const signOut = useCallback(async () => {
    const currentToken = token;

    // Stop the token refresh timers
    stopTokenRefreshTimer();
    stopGoogleTokenRefreshTimer();

    // Clear state
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setIsGuestUser(false);

    // Clear storage
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(IS_GUEST_STORAGE_KEY);

    // Optionally revoke Google token (only for real Google OAuth tokens)
    if (currentToken && currentToken !== MOCK_TOKEN) {
      try {
        await fetch(`https://oauth2.googleapis.com/revoke?token=${currentToken}`, {
          method: 'POST',
        });
      } catch (error) {
        console.error('Error revoking token:', error);
      }
    }
  }, [token, stopTokenRefreshTimer, stopGoogleTokenRefreshTimer]);

  // Create session object for compatibility
  const session = user && token ? { user, access_token: token } : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        token,
        jwtToken,
        loading,
        signInWithGoogle,
        signOut,
        bypassAuth: BYPASS_AUTH,
        testUsers: TEST_USERS,
        isGuestUser,
        shouldAutoBypass: SHOULD_AUTO_BYPASS,
        authError,
        clearAuthError,
        refreshJwtToken,
        refreshGoogleAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
